import React, { useState, useEffect, useCallback } from "react";
import { apiJson, api, API_BASE } from "../../utils/api";
import { useT } from "../../i18n/LanguageContext";

const LOCKED_FIELDS = ["name", "fatherName", "motherName", "gender", "aadhaar"];

const NATIONALITY = ["INDIAN", "OTHER"];
const RELIGION = ["HINDU", "MUSLIM", "CHRISTIAN", "SIKH", "BUDDHIST", "JAIN", "OTHER"];
const GENDER = ["MALE", "FEMALE", "OTHER"];
const CASTE = ["UR", "OBC", "SC", "ST", "BC", "EBC", "EWS", "PH", "URF"];

const AdmissionForm = () => {
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [admission, setAdmission] = useState(null);
  const [student, setStudent] = useState(null);
  const [personalInfo, setPersonalInfo] = useState({});
  const [permanentAddr, setPermanentAddr] = useState({});
  const [commAddr, setCommAddr] = useState({});
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [qualifications, setQualifications] = useState([]);
  const [qualConfigs, setQualConfigs] = useState([]);
  const [formConfig, setFormConfig] = useState([]);
  const [docUploads, setDocUploads] = useState([]);
  const [docRequirements, setDocRequirements] = useState([]);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const fetchDocs = useCallback(async () => {
    try {
      const d = await apiJson("/api/student/admission/documents");
      setDocUploads(d.uploads || []);
      setDocRequirements(d.requirements || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [data, qc, fc] = await Promise.all([
        apiJson("/api/student/admission"),
        apiJson("/api/student/admission/qualification-config"),
        apiJson("/api/student/admission/form-config"),
      ]);
      setStudent(data.student);
      setAdmission(data.admission);
      setQualConfigs(qc.configs || []);
      setFormConfig(fc || []);

      setPersonalInfo({
        name: data.student.name || "",
        fatherName: data.student.fatherName || "",
        motherName: data.student.motherName || "",
        nationality: data.student.nationality || "",
        religion: data.student.religion || "",
        casteCategory: data.student.casteCategory || "",
        gender: data.student.gender || "",
        motherTongue: data.student.motherTongue || "",
        aadhaar: data.student.aadhaar || "",
      });
      setPhotoUrl(data.student.photoUrl || "");

      const perm = data.student.addresses?.find((a) => a.type === "PERMANENT") || {};
      const comm = data.student.addresses?.find((a) => a.type === "COMMUNICATION") || {};
      setPermanentAddr(perm);
      setCommAddr(comm);

      // Build qualifications from configs, merging in any existing AcademicQualification rows by label match
      const existing = data.student.qualifications || [];
      const rows = (qc.configs || []).map((cfg) => {
        const match = existing.find((q) => q.level === cfg.label);
        return {
          configId: cfg.id,
          documentTypeId: cfg.documentTypeId || null,
          label: cfg.label,
          isRequired: cfg.isRequired,
          level: cfg.label, // stored as level on AcademicQualification
          boardName: match?.boardName || "",
          classRollNo: match?.classRollNo || "",
          universityRollNo: match?.universityRollNo || "",
          yearOfPassing: match?.yearOfPassing || "",
          percentage: match?.percentage || "",
        };
      });
      setQualifications(rows);

      await fetchDocs();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchDocs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Helpers driven by FormFieldConfig ────────────────────
  const cfgMap = {};
  for (const f of formConfig) cfgMap[f.fieldKey] = f;
  const isVisible = (key) => (cfgMap[key] ? cfgMap[key].isVisible : true);
  const isRequired = (key) => (cfgMap[key] ? cfgMap[key].isRequired : false);
  const labelOf = (key, fallback) => cfgMap[key]?.label || fallback;
  const isLocked = (key) => admission?.status === "ADMITTED" && LOCKED_FIELDS.includes(key);

  const submitForm = async (isDraft) => {
    setMessage("");
    setError("");
    try {
      const data = await apiJson("/api/student/admission/form", {
        method: "PUT",
        body: JSON.stringify({
          isDraft,
          personalInfo,
          addresses: [
            { type: "PERMANENT", ...permanentAddr },
            { type: "COMMUNICATION", ...(sameAsPermanent ? permanentAddr : commAddr) },
          ],
          qualifications: qualifications.map((q) => ({
            level: q.level,
            boardName: q.boardName,
            classRollNo: q.classRollNo,
            universityRollNo: q.universityRollNo,
            yearOfPassing: q.yearOfPassing,
            percentage: q.percentage,
          })),
        }),
      });
      setMessage(data.message || (isDraft ? "Draft saved" : "Form saved successfully!"));
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitForm(false);
  };

  const handleSaveDraft = () => submitForm(true);

  const updateQual = (index, field, value) => {
    const updated = [...qualifications];
    updated[index] = { ...updated[index], [field]: value };
    setQualifications(updated);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const res = await api("/api/student/photo", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Upload failed");
      setPhotoUrl(data.photoUrl);
      setMessage("Photo uploaded");
    } catch (err) {
      setError(err.message);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleQualDocUpload = async (documentTypeId, file) => {
    if (!file || !documentTypeId) return;
    setError("");
    try {
      const fd = new FormData();
      fd.append("documentTypeId", documentTypeId);
      fd.append("file", file);
      const res = await api("/api/student/admission/documents", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Upload failed");
      setMessage("Document uploaded");
      await fetchDocs();
    } catch (err) {
      setError(err.message);
    }
  };

  const findUploadFor = (docTypeId) => docUploads.find((u) => u.documentTypeId === docTypeId);

  if (loading) return <p className="text-gray-500">{t("common.loading")}</p>;

  const isSubmitted = admission && admission.status !== "PENDING";

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-xl font-bold text-gray-800 mb-4">{t("studentNav.admissionForm")}</h1>

      {admission && (
        <div className="bg-blue-50 p-3 rounded mb-4 text-sm">
          <p><strong>{t("form.session")}:</strong> {admission.academicSession?.name}</p>
          <p><strong>{t("form.degree")}:</strong> {admission.degree?.name}</p>
          <p><strong>{t("form.type")}:</strong> {admission.admissionType}</p>
          <p><strong>{t("form.status")}:</strong> <span className="font-semibold">{t(`status.${admission.status}`, admission.status)}</span></p>
        </div>
      )}

      {message && <div className="bg-green-50 text-green-700 p-3 rounded mb-4 text-sm">{message}</div>}
      {error && <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Info */}
        <section className="bg-white p-4 rounded shadow">
          <h2 className="font-semibold text-gray-700 mb-3">{t("form.personalInformation")}</h2>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("auth.applyId")}</label>
                <input type="text" value={student?.applyId || ""} disabled className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("auth.dateOfBirth")}</label>
                <input type="text" value={student?.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString("en-IN") : ""} disabled className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100" />
              </div>
              <div className="hidden md:block" />
              {isVisible("name") && <Field label={labelOf("name", "Name")} value={personalInfo.name} onChange={(v) => setPersonalInfo({ ...personalInfo, name: v })} required={isRequired("name")} disabled={isLocked("name")} locked={isLocked("name")} />}
              {isVisible("fatherName") && <Field label={labelOf("fatherName", "Father's Name")} value={personalInfo.fatherName} onChange={(v) => setPersonalInfo({ ...personalInfo, fatherName: v })} required={isRequired("fatherName")} disabled={isLocked("fatherName")} locked={isLocked("fatherName")} />}
              {isVisible("motherName") && <Field label={labelOf("motherName", "Mother's Name")} value={personalInfo.motherName} onChange={(v) => setPersonalInfo({ ...personalInfo, motherName: v })} required={isRequired("motherName")} disabled={isLocked("motherName")} locked={isLocked("motherName")} />}
              {isVisible("gender") && <Select label={labelOf("gender", "Gender")} value={personalInfo.gender} options={GENDER} onChange={(v) => setPersonalInfo({ ...personalInfo, gender: v })} required={isRequired("gender")} disabled={isLocked("gender")} locked={isLocked("gender")} />}
              {isVisible("casteCategory") && <Select label={labelOf("casteCategory", "Caste Category")} value={personalInfo.casteCategory} options={CASTE} onChange={(v) => setPersonalInfo({ ...personalInfo, casteCategory: v })} required={isRequired("casteCategory")} />}
              {isVisible("nationality") && <Select label={labelOf("nationality", "Nationality")} value={personalInfo.nationality} options={NATIONALITY} onChange={(v) => setPersonalInfo({ ...personalInfo, nationality: v })} required={isRequired("nationality")} />}
              {isVisible("religion") && <Select label={labelOf("religion", "Religion")} value={personalInfo.religion} options={RELIGION} onChange={(v) => setPersonalInfo({ ...personalInfo, religion: v })} required={isRequired("religion")} />}
              {isVisible("motherTongue") && <Field label={labelOf("motherTongue", "Mother Tongue")} value={personalInfo.motherTongue || ""} onChange={(v) => setPersonalInfo({ ...personalInfo, motherTongue: v })} required={isRequired("motherTongue")} />}
              {isVisible("aadhaar") && (
                <AadhaarBlock
                  value={personalInfo.aadhaar || ""}
                  onChange={(v) => setPersonalInfo({ ...personalInfo, aadhaar: v })}
                  required={isRequired("aadhaar")}
                  disabled={isLocked("aadhaar")}
                  locked={isLocked("aadhaar")}
                  label={labelOf("aadhaar", "Aadhaar")}
                  docRequirement={docRequirements.find((r) => /aadhar|aadhaar/i.test(r.documentType?.name || ""))}
                  upload={docUploads.find((u) => {
                    const req = docRequirements.find((r) => r.documentTypeId === u.documentTypeId);
                    return req && /aadhar|aadhaar/i.test(req.documentType?.name || "");
                  })}
                  onUpload={(f) => {
                    const req = docRequirements.find((r) => /aadhar|aadhaar/i.test(r.documentType?.name || ""));
                    if (req) handleQualDocUpload(req.documentTypeId, f);
                  }}
                />
              )}
            </div>
            {isVisible("photoUrl") && (
              <div className="md:w-40 flex-shrink-0">
                <label className="block text-xs font-medium text-gray-600 mb-1">{labelOf("photoUrl", t("form.photograph"))}</label>
                <div className="border rounded p-2 text-center bg-gray-50">
                  {photoUrl ? (
                    <img src={`${API_BASE}${photoUrl}`} alt="student" className="w-32 h-40 object-cover mx-auto mb-2 border" />
                  ) : (
                    <div className="w-32 h-40 bg-gray-200 mx-auto mb-2 flex items-center justify-center text-xs text-gray-500">{t("form.noPhoto")}</div>
                  )}
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={photoUploading} className="text-xs w-full" />
                  {photoUploading && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Permanent Address */}
        <AddressSection
          t={t}
          title={t("form.permanentAddress")}
          prefix="perm"
          addr={permanentAddr}
          setAddr={setPermanentAddr}
          isVisible={isVisible}
          isRequired={isRequired}
          labelOf={labelOf}
        />

        {/* Communication Address */}
        <AddressSection
          t={t}
          title={t("form.communicationAddress")}
          prefix="comm"
          addr={sameAsPermanent ? permanentAddr : commAddr}
          setAddr={setCommAddr}
          disabled={sameAsPermanent}
          isVisible={isVisible}
          isRequired={isRequired}
          labelOf={labelOf}
          checkbox={
            <label className="inline-flex items-center ml-4 text-sm text-gray-600 font-normal cursor-pointer">
              <input
                type="checkbox"
                checked={sameAsPermanent}
                onChange={(e) => {
                  setSameAsPermanent(e.target.checked);
                  if (e.target.checked) setCommAddr({ ...permanentAddr });
                }}
                className="mr-1.5"
              />
              {t("form.sameAsPermanent")}
            </label>
          }
        />

        {/* Academic Qualifications (dynamic per admission type) */}
        {qualifications.length > 0 && (
          <section className="bg-white p-4 rounded shadow">
            <h2 className="font-semibold text-gray-700 mb-3">
              {t("form.academicQualifications")}
              {admission?.status === "ADMITTED" && (
                <span className="ml-2 text-xs font-normal text-gray-500">🔒 {t("form.lockedAfterAdmission")}</span>
              )}
            </h2>
            {qualifications.map((qual, idx) => {
              const upload = qual.documentTypeId ? findUploadFor(qual.documentTypeId) : null;
              const qualLocked = admission?.status === "ADMITTED";
              return (
                <div key={qual.configId || idx} className="mb-4 p-3 bg-gray-50 rounded">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">
                    {qual.label}
                    {qual.isRequired && <span className="text-red-500 ml-1">*</span>}
                    {qualLocked && <span className="ml-2 text-gray-500" title="Locked after admission">🔒</span>}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label={t("form.board")} value={qual.boardName || ""} onChange={(v) => updateQual(idx, "boardName", v)} disabled={qualLocked} />
                    <Field label={t("form.rollNo")} value={qual.classRollNo || ""} onChange={(v) => updateQual(idx, "classRollNo", v)} disabled={qualLocked} />
                    <Field label={t("form.yearOfPassing")} type="number" value={qual.yearOfPassing || ""} onChange={(v) => updateQual(idx, "yearOfPassing", v)} disabled={qualLocked} />
                    <Field label={t("form.percentage")} type="number" step="0.01" value={qual.percentage || ""} onChange={(v) => updateQual(idx, "percentage", v)} disabled={qualLocked} />
                  </div>
                  {qual.documentTypeId && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t("form.certificate")}</label>
                      {upload ? (
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-green-700">✓ {t("form.uploaded")}</span>
                          <a href={`${API_BASE}${upload.fileUrl}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{t("common.view")}</a>
                          <a href={`${API_BASE}${upload.fileUrl}`} download className="text-indigo-600 hover:underline">{t("common.download")}</a>
                          {!qualLocked && (
                            <label className="text-gray-500 cursor-pointer hover:underline">
                              {t("common.replace")}
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => handleQualDocUpload(qual.documentTypeId, e.target.files?.[0])}
                              />
                            </label>
                          )}
                        </div>
                      ) : qualLocked ? (
                        <p className="text-xs text-gray-500 italic">{t("form.noFileUploaded")}</p>
                      ) : (
                        <input
                          type="file"
                          onChange={(e) => handleQualDocUpload(qual.documentTypeId, e.target.files?.[0])}
                          className="text-sm"
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 transition"
          >
            {isSubmitted ? t("common.updateForm") : t("common.submitForm")}
          </button>
          {!isSubmitted && (
            <button
              type="button"
              onClick={handleSaveDraft}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-300 transition"
            >
              {t("common.saveDraft")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

// Aadhaar = number + linked Aadhar Card upload widget (visible together)
const AadhaarBlock = ({ value, onChange, required, disabled, locked, label, docRequirement, upload, onUpload }) => (
  <div className="sm:col-span-2 md:col-span-2">
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label}{required && <span className="text-red-500 ml-1">*</span>}{locked && <span title="Locked after admission"> 🔒</span>}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
      placeholder="Enter Aadhaar number"
      className={`w-full px-2 py-1.5 border rounded text-sm ${disabled ? "bg-gray-100" : ""}`}
    />
    {docRequirement && (
      <div className="mt-1 text-xs text-gray-600">
        <span className="block mb-0.5">Aadhar Card {docRequirement.isRequired && <span className="text-red-500">*</span>}</span>
        {upload ? (
          <div className="flex items-center gap-2">
            <span className="text-green-700">✓ Uploaded</span>
            <a href={`${API_BASE}${upload.fileUrl}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">View</a>
            <a href={`${API_BASE}${upload.fileUrl}`} download className="text-indigo-600 hover:underline">Download</a>
            {!disabled && (
              <label className="text-gray-500 cursor-pointer hover:underline">
                Replace
                <input type="file" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
              </label>
            )}
          </div>
        ) : disabled ? (
          <span className="text-gray-500 italic">No file uploaded</span>
        ) : (
          <input type="file" onChange={(e) => onUpload(e.target.files?.[0])} className="text-xs" />
        )}
      </div>
    )}
  </div>
);

const Select = ({ label, value, options, onChange, required, disabled, locked }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label}{required && <span className="text-red-500 ml-1">*</span>}{locked && <span title="Locked after admission"> 🔒</span>}
    </label>
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
      className={`w-full px-2 py-1.5 border rounded text-sm ${disabled ? "bg-gray-100" : "bg-white"}`}
    >
      <option value="">-- Select --</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

const Field = ({ label, value, onChange, type = "text", required, step, disabled, locked }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label}{required && <span className="text-red-500 ml-1">*</span>}{locked && <span title="Locked after admission"> 🔒</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      step={step}
      disabled={disabled}
      className={`w-full px-2 py-1.5 border rounded text-sm ${disabled ? "bg-gray-100" : ""}`}
    />
  </div>
);

const AddressSection = ({ t, title, prefix, addr, setAddr, disabled, checkbox, isVisible, isRequired, labelOf }) => {
  const k = (suffix) => `${prefix}.${suffix}`;
  const tt = t || ((_k, fb) => fb);
  const fields = [
    { key: "state", fallback: tt("form.state", "State") },
    { key: "district", fallback: tt("form.district", "District") },
    { key: "pinCode", fallback: tt("form.pinCode", "Pin Code") },
    { key: "mobileNo", fallback: tt("form.mobileNo", "Mobile No") },
    { key: "phoneNo", fallback: tt("form.phoneNo", "Phone No") },
  ];
  const visibleFields = fields.filter((f) => isVisible(k(f.key)));
  const showAddress = isVisible(k("address"));
  if (visibleFields.length === 0 && !showAddress) return null;
  return (
    <section className="bg-white p-4 rounded shadow">
      <h2 className="font-semibold text-gray-700 mb-3">{title}{checkbox}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {visibleFields.map((f) => (
          <Field
            key={f.key}
            label={labelOf(k(f.key), f.fallback)}
            value={addr[f.key] || ""}
            onChange={(v) => setAddr({ ...addr, [f.key]: v })}
            disabled={disabled}
            required={isRequired(k(f.key))}
          />
        ))}
      </div>
      {showAddress && (
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {labelOf(k("address"), tt("form.address", "Address"))}
            {isRequired(k("address")) && <span className="text-red-500 ml-1">*</span>}
          </label>
          <textarea
            value={addr.address || ""}
            onChange={(e) => setAddr({ ...addr, address: e.target.value })}
            rows={2}
            disabled={disabled}
            required={isRequired(k("address"))}
            className={`w-full px-2 py-1.5 border rounded text-sm ${disabled ? "bg-gray-100" : ""}`}
          />
        </div>
      )}
    </section>
  );
};

export default AdmissionForm;
